import Add from "@mui/icons-material/Add";
import CreditCard from "@mui/icons-material/CreditCard";
import Subscriptions from "@mui/icons-material/Subscriptions";
import Key from "@mui/icons-material/Key";
import MoreVert from "@mui/icons-material/MoreVert";
import PhotoCamera from "@mui/icons-material/PhotoCamera";
import Verified from "@mui/icons-material/Verified";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  IconButton,
  LinearProgress,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { loadStripe } from "@stripe/stripe-js";
import axios from "axios";
import { User } from "../../utils";
import { useEffect, useRef, useState } from "react";
import capture from "../../capture";
import LoadingButton from "../../components/LoadingButton";
import McpSetupSection from "../../components/McpSetupSection";
import VerificationCodeInput from "../../components/VerificationCodeInput";
import { hostname, parseError, stripePublicId, getSupportContactInfo, isSafeUrl } from "../../utils";
import {
  getSubscriptionDisplayName,
  isFreeUser,
} from "../../utils/subscriptionUtils";
import SubscriptionDialog from "./SubscriptionDialog";
import TokenTopupDialog from "./dialogs/TokenTopupDialog";
import PersonalAccessKeysDialog from "./dialogs/PersonalAccessKeysDialog";
import AddOnsDialog from "./dialogs/AddOnsDialog";
import { useWhitelabel } from "../../context/WhitelabelContext";

const stripe = loadStripe(stripePublicId);

export default ({
  currentUser,
  token,
  getToken,
  eventListener,
  mode,
}: {
  currentUser: any | User;
  token: any | string;
  getToken: () => Promise<string>;
  eventListener: Function;
  mode: string;
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [usage, setUsage] = useState<any>({});
  const [modalOpen, setModalOpen] = useState(false);
  const [topupDialogOpen, setTopupDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState<any>({});
  const [currentPlan, setCurrentPlan] = useState("free");
  const [editingName, setEditingName] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [photoURL, setPhotoURL] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { whitelabelSettings } = useWhitelabel();

  // MFA state
  const [mfaStatus, setMfaStatus] = useState<{
    mfaRequired: boolean;
    mfaEnabled: boolean;
    companies: { id: string; name: string }[];
  }>({ mfaRequired: false, mfaEnabled: false, companies: [] });
  const [mfaSetupDialogOpen, setMfaSetupDialogOpen] = useState(false);
  const [mfaDisableDialogOpen, setMfaDisableDialogOpen] = useState(false);
  const [mfaQrCode, setMfaQrCode] = useState("");
  const [mfaSecret, setMfaSecret] = useState("");
  const [mfaSetupStep, setMfaSetupStep] = useState<"scan" | "verify">("scan");
  const [mfaLoading, setMfaLoading] = useState(false);
  const [mfaError, setMfaError] = useState("");
  const [mfaDisableCode, setMfaDisableCode] = useState("");
  const [openingBillingPortal, setOpeningBillingPortal] = useState(false);
  const [hasStripeCustomer, setHasStripeCustomer] = useState(false);
  const [personalAccessKeysDialogOpen, setPersonalAccessKeysDialogOpen] = useState(false);
  const [addOnsDialogOpen, setAddOnsDialogOpen] = useState(false);
  const [hasAddOns, setHasAddOns] = useState(false);

  const getUsage = async () => {
    const currentToken = await getToken();
    await axios
      .get(`${hostname}/usage`, {
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      })
      .then((res) => {
        capture("Usage", res.data.usage);
        console.log("Profile usage data:", { isOrgManaged: res.data.usage?.isOrgManaged, subscriptionType: res.data.usage?.subscriptionType });
        setUsage(res.data.usage);
        if (res.data.usage && res.data.usage.subscriptionType) {
          setCurrentPlan(res.data.usage.subscriptionType.toLowerCase());
        }
        // Set photo and display name from usage data
        if (res.data.usage) {
          setPhotoURL(res.data.usage.photo || "");
          setDisplayName(res.data.usage.displayName || "");
        }
      })
      .catch((err) => {
        eventListener({
          type: "SET_ERROR",
          error: parseError(err),
        });
      });
  };

  const getData = async () => {
    setLoading(true);
    await getUsage();
    await getSettings();
    await getMfaStatus();
    await checkStripeCustomer();
    await checkAddOns();
    setLoading(false);
  };

  const checkAddOns = async () => {
    try {
      const response = await axios.get(`${hostname}/service-packages`, {
        params: { domain: window.location.hostname },
      });
      const hasPackages = response.data.success && response.data.packages?.length > 0;
      console.log("checkAddOns result:", { hasPackages, packages: response.data.packages?.length });
      setHasAddOns(hasPackages);
    } catch (err) {
      console.error("Error checking add-ons:", err);
      setHasAddOns(false);
    }
  };

  const getMfaStatus = async () => {
    try {
      const currentToken = await getToken();
      const response = await axios.get(`${hostname}/auth/mfa/status`, {
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      });
      setMfaStatus(response.data);
    } catch (err) {
      console.error("Error getting MFA status:", err);
    }
  };

  const checkStripeCustomer = async () => {
    try {
      const currentToken = await getToken();
      const response = await axios.get(`${hostname}/subscriptions/has-stripe-customer`, {
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      });
      setHasStripeCustomer(response.data.hasStripeCustomer || false);
    } catch (err) {
      console.error("Error checking Stripe customer:", err);
      setHasStripeCustomer(false);
    }
  };

  const handleSetupMfa = async () => {
    setMfaLoading(true);
    setMfaError("");
    try {
      const currentToken = await getToken();
      const response = await axios.post(
        `${hostname}/auth/mfa/setup`,
        {},
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        }
      );

      if (response.data.success) {
        setMfaQrCode(response.data.qrCode);
        setMfaSecret(response.data.secret);
        setMfaSetupStep("scan");
        setMfaSetupDialogOpen(true);
      }
    } catch (err: any) {
      setMfaError(err.response?.data?.error || "Failed to setup MFA");
    } finally {
      setMfaLoading(false);
    }
  };

  const handleVerifyMfaSetup = async (code: string) => {
    setMfaLoading(true);
    setMfaError("");
    try {
      const currentToken = await getToken();
      const response = await axios.post(
        `${hostname}/auth/mfa/verify-setup`,
        { code },
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        }
      );

      if (response.data.success) {
        setMfaSetupDialogOpen(false);
        setMfaStatus({ ...mfaStatus, mfaEnabled: true });
        eventListener({
          type: "SET_SUCCESS",
          message: "MFA has been enabled successfully",
        });
      }
    } catch (err: any) {
      setMfaError(err.response?.data?.error || "Invalid verification code");
    } finally {
      setMfaLoading(false);
    }
  };

  const handleDisableMfa = async () => {
    if (!mfaDisableCode || mfaDisableCode.length !== 6) {
      setMfaError("Please enter a 6-digit code");
      return;
    }

    setMfaLoading(true);
    setMfaError("");
    try {
      const currentToken = await getToken();
      const response = await axios.post(
        `${hostname}/auth/mfa/disable`,
        { code: mfaDisableCode },
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        }
      );

      if (response.data.success) {
        setMfaDisableDialogOpen(false);
        setMfaDisableCode("");
        setMfaStatus({ ...mfaStatus, mfaEnabled: false });
        eventListener({
          type: "SET_SUCCESS",
          message: "MFA has been disabled",
        });
      }
    } catch (err: any) {
      setMfaError(err.response?.data?.error || "Failed to disable MFA");
    } finally {
      setMfaLoading(false);
    }
  };

  const handleOpenBillingPortal = async () => {
    setOpeningBillingPortal(true);
    try {
      const currentToken = await getToken();
      const response = await axios.post(
        `${hostname}/subscriptions/billing-portal`,
        {
          return_url: window.location.href,
        },
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        }
      );

      if (response.data.success && response.data.url) {
        // Redirect to Stripe Billing Portal
        const safeUrlPattern = /^https:\/\/[a-zA-Z0-9][a-zA-Z0-9.\-:/\\?&=%_+~#]+$/;
        if (safeUrlPattern.test(response.data.url)) window.location.href = response.data.url;
      }
    } catch (err: any) {
      eventListener({
        type: "SET_ERROR",
        error: err.response?.data?.error || "Failed to open billing portal",
      });
      setOpeningBillingPortal(false);
    }
  };

  const getCurrrentUsage = (): any => {
    if (!usage) return 0;
    if ("creditUsage" in usage) {
      return parseFloat(usage["creditUsage"]).toFixed(2);
    }
    return 0;
  };

  const getSeats = (): number => {
    if (!usage) return 1;
    return usage.seats || 1;
  };

  const getSeatsUsed = (): number => {
    if (!usage) return 0;
    return usage.seatsUsed || 0;
  };

  const getCompanies = (): number => {
    if (!usage) return 1;
    return usage.companyLimit || 1;
  };

  const getCompaniesUsed = (): number => {
    if (!usage) return 0;
    return usage.companiesUsed || 0;
  };

  const getTopupCredits = (): number => {
    if (!usage) return 0;
    return parseFloat(usage.topupCredits || 0);
  };

  const isPaidUser = (): boolean => {
    if (!usage) return false;
    const subType = (usage.subscriptionType || "free").toLowerCase();
    return subType !== "free" && subType !== "";
  };

  const getLimit = (): number => {
    if (!usage) {
      return 50;
    }
    const limit = usage["creditLimit"] || 50;
    return limit;
  };

  const getProgress = (): number => {
    return (getCurrrentUsage() / getLimit()) * 100;
  };

  const getSubscriptionType = (): string => {
    if (!usage) {
      return "Free Tier";
    }
    // If backend provided a friendly tier name, use it
    if (usage.subscriptionTierName) {
      return usage.subscriptionTierName;
    }
    if (isFreeUser(usage.subscriptionType)) {
      return "Free Tier";
    }
    return getSubscriptionDisplayName(usage.subscriptionType);
  };

  const getSettings = async () => {
    const currentToken = await getToken();
    await axios
      .get(`${hostname}/users/settings`, {
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      })
      .then((res) => {
        setSettings(res.data);
      })
      .catch((err) => {
        eventListener({
          type: "SET_ERROR",
          error: parseError(err),
        });
      });
  };

  const handleEmailNotificationsToggle = async (value: boolean) => {
    setSettings({ ...settings, emailNotifications: value });
    const currentToken = await getToken();
    await axios
      .post(
        `${hostname}/users/settings`,
        {
          settings: {
            emailNotifications: value,
          },
        },
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        }
      )
      .then((res) => {
        setSettings(res.data);
      })
      .catch((err) => {
        eventListener({
          type: "SET_ERROR",
          error: parseError(err),
        });
      });
  };

  const handleSaveDisplayName = async () => {
    if (!displayName.trim()) {
      eventListener({
        type: "SET_ERROR",
        error: "Display name cannot be empty",
      });
      return;
    }

    setSavingProfile(true);
    try {
      const currentToken = await getToken();
      const response = await axios.post(
        `${hostname}/users/profile`,
        { displayName: displayName.trim() },
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        }
      );

      // Update local state with response
      if (response.data.displayName) {
        setDisplayName(response.data.displayName);
      }

      eventListener({
        type: "SET_SUCCESS",
        message: "Display name updated successfully",
      });
      setEditingName(false);
    } catch (err) {
      eventListener({
        type: "SET_ERROR",
        error: parseError(err),
      });
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePhotoUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      eventListener({
        type: "SET_ERROR",
        error: "Please select an image file",
      });
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      eventListener({
        type: "SET_ERROR",
        error: "Image size must be less than 5MB",
      });
      return;
    }

    setUploadingPhoto(true);
    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64Data = reader.result as string;

        const currentToken = await getToken();

        // Upload photo using platform images endpoint
        const uploadResponse = await axios.post(
          `${hostname}/platform-images`,
          {
            url: base64Data,
            name: "Profile Photo",
            public: true,
          },
          {
            headers: {
              Authorization: `Bearer ${currentToken}`,
            },
          }
        );

        const newPhotoURL = uploadResponse.data.url;

        // Update profile with new photo URL
        const profileResponse = await axios.post(
          `${hostname}/users/profile`,
          { photoURL: newPhotoURL },
          {
            headers: {
              Authorization: `Bearer ${currentToken}`,
            },
          }
        );

        // Update local state with new photo URL
        if (profileResponse.data.photoURL) {
          setPhotoURL(profileResponse.data.photoURL);
        }

        eventListener({
          type: "SET_SUCCESS",
          message: "Profile photo updated successfully",
        });
        setUploadingPhoto(false);
      };
      reader.onerror = () => {
        eventListener({
          type: "SET_ERROR",
          error: "Failed to read image file",
        });
        setUploadingPhoto(false);
      };
      reader.readAsDataURL(file);
    } catch (err) {
      eventListener({
        type: "SET_ERROR",
        error: parseError(err),
      });
      setUploadingPhoto(false);
    }
  };

  useEffect(() => {
    if (currentUser.uid) {
      getData();

      // Check if returning from payment redirect
      const urlParams = new URLSearchParams(window.location.search);
      const paymentRedirect = urlParams.get("payment_redirect");
      const paymentIntentClientSecret = urlParams.get(
        "payment_intent_client_secret"
      );

      if (paymentRedirect === "true" && paymentIntentClientSecret) {
        // Open the subscription dialog to handle the redirect
        setModalOpen(true);
      }
    }
  }, [currentUser.uid]);

  if (loading) {
    return <LinearProgress />;
  }

  return (
    <Box sx={{ p: 5 }}>
        <Box sx={{ position: "relative", display: "inline-block" }} data-tutorial="account-info">
          <Avatar
            alt={displayName}
            sx={{ width: 80, height: 80 }}
            src={photoURL}
          />
          <IconButton
            color="primary"
            sx={{
              position: "absolute",
              bottom: -4,
              right: -4,
              width: 32,
              height: 32,
            }}
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadingPhoto}
          >
            <PhotoCamera fontSize="small" />
          </IconButton>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={handlePhotoUpload}
          />
        </Box>

        {editingName ? (
          <Box
            sx={{
              mt: 2,
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              gap: 1,
              alignItems: isMobile ? "stretch" : "center",
            }}
          >
            <TextField
              size="small"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Enter your name"
              autoFocus
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  handleSaveDisplayName();
                }
              }}
              sx={{ flexGrow: isMobile ? 0 : 1 }}
            />
            <Box sx={{ display: "flex", gap: 1 }}>
              <LoadingButton
                size="small"
                variant="contained"
                onClick={handleSaveDisplayName}
                loading={savingProfile}
              >
                Save
              </LoadingButton>
              <Button
                size="small"
                onClick={() => {
                  setEditingName(false);
                  setDisplayName(usage.displayName || "");
                }}
                disabled={savingProfile}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        ) : (
          <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="h6">{displayName || "No name set"}</Typography>
            <IconButton
              size="small"
              onClick={() => setEditingName(true)}
              sx={{ ml: 1 }}
            >
              <MoreVert fontSize="small" />
            </IconButton>
          </Box>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {usage.email || currentUser.email}
        </Typography>
        <Box data-tutorial="subscription-info">
          <Typography variant="body1" style={{ marginTop: 20 }}>
            Subscription Type:{" "}
            {getSubscriptionType() !== "Free Tier" && (
              <Verified fontSize="small" style={{ verticalAlign: -4 }} />
            )}{" "}
            {getSubscriptionType()}
          </Typography>
          <Typography style={{ marginTop: 20 }} variant="body1">
            Monthly credits
          </Typography>
        <Typography variant="h5" fontWeight="500">
          {Math.round(getCurrrentUsage()).toLocaleString()} / {getLimit().toLocaleString()}
        </Typography>
        <LinearProgress
          sx={{ mt: 1, width: "50%", maxWidth: 400 }}
          variant="determinate"
          value={getProgress()}
        />
        <Typography style={{ marginTop: 20 }} variant="body1">
          Seats occupied
          <Tooltip title="Total number of unique users across all your companies. Each user with access to any of your companies counts as one seat.">
            <Chip
              label="?"
              size="small"
              sx={{ ml: 1, height: 18, fontSize: "0.7rem", cursor: "help" }}
            />
          </Tooltip>
        </Typography>
        <Typography variant="h5" fontWeight="500">
          {getSeatsUsed()} / {getSeats()}
        </Typography>
        <Typography style={{ marginTop: 20 }} variant="body1">
          Active companies
          <Tooltip title="Number of companies you own. To transfer ownership of a company, go to the company settings from the edit company menu.">
            <Chip
              label="?"
              size="small"
              sx={{ ml: 1, height: 18, fontSize: "0.7rem", cursor: "help" }}
            />
          </Tooltip>
        </Typography>
        <Typography variant="h5" fontWeight="500">
          {getCompaniesUsed()} / {getCompanies()}
        </Typography>
        <Typography style={{ marginTop: 20 }} variant="body1">
          Top-up credits
          <Tooltip title="Top-up credits never expire and are only used after your monthly credits are depleted. An active subscription is required to use them.">
            <Chip
              label="?"
              size="small"
              sx={{ ml: 1, height: 18, fontSize: "0.7rem", cursor: "help" }}
            />
          </Tooltip>
        </Typography>
        <Typography variant="h5" fontWeight="500">
          {getTopupCredits().toLocaleString()}
        </Typography>
        </Box>
        {!loading && !usage.isOrgManaged && (
        <Box
          data-tutorial="billing-info"
          sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            gap: 2,
            mt: 2,
            alignItems: isMobile ? "stretch" : "center",
          }}
        >
          <Button
            variant="contained"
            startIcon={<Subscriptions />}
            onClick={() => {
              // If custom subscription is enabled, open mailto link
              if (whitelabelSettings?.customSubscriptionEnabled) {
                const salesEmail = whitelabelSettings?.salesEmail || getSupportContactInfo(whitelabelSettings);
                window.open(`mailto:${salesEmail}?subject=Subscription Inquiry`, "_blank");
              } else {
                setModalOpen(true);
              }
            }}
          >
            {whitelabelSettings?.customSubscriptionEnabled ? "Contact Sales" : "Subscription"}
          </Button>
          <Button
            variant="outlined"
            startIcon={<Add />}
            onClick={() => setTopupDialogOpen(true)}
            disabled={!!whitelabelSettings?.customSubscriptionEnabled}
          >
            Add Credits
          </Button>
          {hasAddOns && isPaidUser() && (
            <Button
              variant="outlined"
              startIcon={<Add />}
              onClick={() => setAddOnsDialogOpen(true)}
              disabled={!!whitelabelSettings?.customSubscriptionEnabled}
            >
              Add-ons
            </Button>
          )}
          {isPaidUser() && hasStripeCustomer && (
            <LoadingButton
              variant="outlined"
              startIcon={<CreditCard />}
              onClick={handleOpenBillingPortal}
              loading={openingBillingPortal}
            >
              Update Payment Method
            </LoadingButton>
          )}
        </Box>
        )}
        <Typography sx={{ mt: 6 }} variant="body1">
          Settings
        </Typography>
        <FormGroup sx={{ mt: 1 }}>
          <FormControlLabel
            control={<Switch checked={settings.emailNotifications} />}
            onChange={(e: any) =>
              handleEmailNotificationsToggle(e.target.checked)
            }
            label="Email Notifications"
          />
        </FormGroup>

        <Typography sx={{ mt: 6 }} variant="body1">
          Security
        </Typography>
        <FormGroup sx={{ mt: 1 }}>
          <FormControlLabel
            control={
              <Switch
                checked={mfaStatus.mfaEnabled}
                onChange={(e: any) => {
                  if (e.target.checked) {
                    handleSetupMfa();
                  } else {
                    if (mfaStatus.mfaRequired) {
                      eventListener({
                        type: "SET_ERROR",
                        error: "MFA cannot be disabled (required by company policy)",
                      });
                    } else {
                      setMfaError("");
                      setMfaDisableCode("");
                      setMfaDisableDialogOpen(true);
                    }
                  }
                }}
                disabled={mfaLoading || (mfaStatus.mfaRequired && mfaStatus.mfaEnabled)}
              />
            }
            label="Two-Factor Authentication (MFA)"
          />
        </FormGroup>
        {mfaStatus.mfaRequired && !mfaStatus.mfaEnabled && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            MFA is required by one or more of your companies. Please enable MFA to continue using the platform.
          </Alert>
        )}

        <Typography sx={{ mt: 6 }} variant="body1">
          API Access
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1 }}>
          Personal access keys can be used to authenticate against the API with access to all your companies.
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Key />}
          onClick={() => setPersonalAccessKeysDialogOpen(true)}
        >
          Manage Personal Access Keys
        </Button>

        <McpSetupSection variant="personalAccessKey" />

      {/* MFA Setup Dialog */}
      <Dialog
        open={mfaSetupDialogOpen}
        onClose={() => {
          setMfaSetupDialogOpen(false);
          setMfaError("");
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Set Up Two-Factor Authentication
          <IconButton
            aria-label="close"
            onClick={() => {
              setMfaSetupDialogOpen(false);
              setMfaError("");
            }}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            ×
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {mfaError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setMfaError("")}>
              {mfaError}
            </Alert>
          )}

          {mfaSetupStep === "scan" ? (
            <>
              <Typography variant="body1" gutterBottom>
                <strong>Step 1:</strong> Scan the QR code with your authenticator app
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Use Google Authenticator, Authy, or any TOTP-compatible app
              </Typography>

              <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
                <Box
                  component="img"
                  src={mfaQrCode}
                  alt="MFA QR Code"
                  sx={{
                    width: 200,
                    height: 200,
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                  }}
                />
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Can't scan? Enter this code manually:
              </Typography>
              <Box
                sx={{
                  p: 1.5,
                  bgcolor: "grey.100",
                  borderRadius: 1,
                  fontFamily: "monospace",
                  fontSize: "0.9rem",
                  wordBreak: "break-all",
                  textAlign: "center",
                }}
              >
                {mfaSecret}
              </Box>
            </>
          ) : (
            <>
              <Typography variant="body1" gutterBottom>
                <strong>Step 2:</strong> Enter the 6-digit code from your app
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                This confirms your authenticator is set up correctly
              </Typography>

              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <VerificationCodeInput
                  onComplete={handleVerifyMfaSetup}
                  disabled={mfaLoading}
                  error={!!mfaError}
                  helperText={mfaLoading ? "Verifying..." : ""}
                />
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          {mfaSetupStep === "scan" ? (
            <Button onClick={() => setMfaSetupStep("verify")} variant="contained">
              Continue
            </Button>
          ) : (
            <Button onClick={() => setMfaSetupStep("scan")} disabled={mfaLoading}>
              Back
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* MFA Disable Dialog */}
      <Dialog
        open={mfaDisableDialogOpen}
        onClose={() => {
          setMfaDisableDialogOpen(false);
          setMfaError("");
          setMfaDisableCode("");
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Disable Two-Factor Authentication</DialogTitle>
        <DialogContent>
          {mfaError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setMfaError("")}>
              {mfaError}
            </Alert>
          )}
          <Typography variant="body2" sx={{ mb: 2 }}>
            Enter the 6-digit code from your authenticator app to disable MFA.
          </Typography>
          <TextField
            fullWidth
            label="Verification Code"
            value={mfaDisableCode}
            onChange={(e) => setMfaDisableCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            placeholder="000000"
            inputProps={{ maxLength: 6 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setMfaDisableDialogOpen(false);
              setMfaError("");
              setMfaDisableCode("");
            }}
          >
            Cancel
          </Button>
          <LoadingButton
            onClick={handleDisableMfa}
            color="error"
            variant="contained"
            loading={mfaLoading}
          >
            Disable MFA
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {modalOpen && (
        <SubscriptionDialog
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          currentUser={currentUser}
          getUsage={getUsage}
          token={token}
          getToken={getToken}
          eventListener={eventListener}
          mode={mode}
          currentPlan={currentPlan}
          hasExistingSubscription={currentPlan !== "free"}
          currentSubscriptionPlan={currentPlan}
          currentSubscriptionConfig={{
            seats: usage?.seats || 1,
            companies: usage?.companyLimit || 1,
            creditPackages: usage?.creditPackages || 0,
          }}
          onSubscriptionUpdate={() => {
            // Refresh data after subscription update
            getData();
          }}
        />
      )}

      <TokenTopupDialog
        open={topupDialogOpen}
        onClose={() => setTopupDialogOpen(false)}
        getToken={getToken}
        eventListener={eventListener}
        onSuccess={() => {
          // Refresh data after top-up purchase
          getData();
        }}
        currentTopupCredits={getTopupCredits()}
        isPaidUser={isPaidUser()}
        mode={mode}
        onOpenSubscription={() => setModalOpen(true)}
      />

      <PersonalAccessKeysDialog
        open={personalAccessKeysDialogOpen}
        onClose={() => setPersonalAccessKeysDialogOpen(false)}
        getToken={getToken}
        eventListener={eventListener}
      />

      <AddOnsDialog
        open={addOnsDialogOpen}
        onClose={() => setAddOnsDialogOpen(false)}
        getToken={getToken}
        eventListener={eventListener}
        onSuccess={() => {
          // Refresh data after add-ons purchase
          getData();
        }}
        isPaidUser={isPaidUser()}
        mode={mode}
        onOpenSubscription={() => setModalOpen(true)}
      />
    </Box>
  );
};
